import styled from '@emotion/styled';
import { device, Segment, typography, useRevealTrail } from '@landsbankinn/ui';
import Link from 'next/link';
import React from 'react';

interface IProps {
  heading: string | null;
  children: React.ReactNode;
  link?: string;
  length: number;
  featured?: boolean;
}

const Host = styled(Segment)`
  background-color: ${(props) => props.theme.colors.backgroundAlt};
  margin-top: 4em;
`;

const Group = styled.div<{ featured?: boolean; length: number }>`
  margin-bottom: 3em;
  > * {
    margin-bottom: 3em;
  }

  > *:last-child {
    margin-bottom: 0;
  }

  @media ${device.tablet} {
    margin-bottom: 0;
    display: -ms-grid;
    display: grid;
    grid-template-columns: ${({ featured, length }) =>
      featured
        ? '2fr 1fr 1fr'
        : length === 5
        ? '1fr 1fr 1fr 1fr 1fr'
        : '1fr 1fr'};
    --ms-grid-columns: ${({ featured, length }) =>
      featured
        ? '2fr 30px 1fr 30px 1fr'
        : length === 5
        ? '1fr 30px 1fr 30px 1fr 30px 1fr 30px 1fr'
        : '1fr 30px 1fr 30px 1fr'};
    grid-template-rows: ${({ featured }) => (featured ? '1fr 1fr' : '1fr')};
    --ms-grid-rows: ${({ featured }) => (featured ? '1fr 36px 1fr' : '1fr')};
    grid-template-areas: ${({ featured, length }) =>
      featured
        ? `"featured item2 item3"
        "featured item4 item5"`
        : length === 5
        ? `"item1 item2 item3 item4 item5"`
        : `"item1 item2"
        "item3 item4"`};
    grid-gap: 36px 30px;

    > *:first-child {
      grid-area: ${({ featured }) => (featured ? 'featured' : 'item1')};
      -ms-grid-row: 1;
      -ms-grid-row-span: ${({ featured }) => (featured ? '3' : '1')};
      -ms-grid-column: 1;
    }
    > *:nth-child(2) {
      -ms-grid-row: 1;
      -ms-grid-column: 3;
      grid-area: item2;
    }
    > *:nth-child(3) {
      -ms-grid-row: 1;
      -ms-grid-column: 5;
      grid-area: item3;
    }
    > *:nth-child(4) {
      -ms-grid-row: ${({ featured }) => (featured ? '3' : '1')};
      -ms-grid-column: ${({ featured }) => (featured ? '3' : '7')};
      grid-area: item4;
    }
    > *:nth-child(5) {
      -ms-grid-row: ${({ featured }) => (featured ? '3' : '1')};
      -ms-grid-column: ${({ featured }) => (featured ? '5' : '9')};
      grid-area: item5;
    }

    > * {
      margin-bottom: 2em;
    }

    > *:last-child {
      margin-bottom: 0;
    }
  }

  @media ${device.desktop} {
    grid-template-columns: ${({ featured, length }) =>
      featured
        ? '2fr 1fr 1fr'
        : length === 5
        ? '1fr 1fr 1fr 1fr 1fr'
        : '1fr 1fr 1fr 1fr'};
    --ms-grid-columns: ${({ featured, length }) =>
      featured
        ? '2fr 30px 1fr 30px 1fr'
        : length === 5
        ? '1fr 30px 1fr 30px 1fr 30px 1fr 30px 1fr'
        : '1fr 30px 1fr 30px 1fr 30px 1fr'};
    > *:nth-child(4) {
      display: block;
    }
    grid-template-areas: ${({ featured, length }) =>
      featured
        ? `"featured item2 item3"
        "featured item4 item5"`
        : length === 5
        ? `"item1 item2 item3 item4 item5"`
        : `"item1 item2 item3 item4"`};
  }
`;

const Title = styled.h2`
  ${typography.h2};
  margin-bottom: 1.2em;
  margin-top: 1em;

  @media ${device.tablet} {
    ${typography.h3};
    margin-bottom: 60px;
    margin-top: 0;
  }
`;

export const CardNewsGroup = ({
  children,
  featured,
  heading,
  link,
  length,
}: IProps) => {
  const { ref, itemsWithStyleProp } = useRevealTrail(children);
  return (
    <Host isGray>
      <div ref={ref}>
        <Link href={link as any} passHref>
          <a>
            <Title>{heading}</Title>
          </a>
        </Link>
        <Group featured={featured} length={length}>
          {itemsWithStyleProp}
        </Group>
      </div>
    </Host>
  );
};
